import WindowViewModelT from '@/ViewModels/Controls/WindowViewModel';
import LabelViewModelT from '@/ViewModels/Controls/LabelViewModel';
import ButtonViewModelT from '@/ViewModels/Controls/ButtonViewModel';
import PasswordBoxViewModelT from '@/ViewModels/Controls/PasswordBoxViewModel';
import MessageBoxViewModelT from '@/ViewModels/Controls/MessageBoxViewModel';
import ImageViewModelT from '@/ViewModels/Controls/ImageViewModel';
import ViewViewModel from '@/ViewModels/ViewViewModel';
import ComponentManager from '@/Management/ComponentManager';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import RADApplication from '@/Management/RADApplication';
import NavigationService from '@/Management/NavigationService';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import ResourceManager from '@/Management/ResourceManager';
import TwoFactorTokenDialogViewFactory from '@/Components/TwoFaktorAuth/TokenDialog/TwoFactorTokenDialogViewFactory';
import { IChangePasswordResponseDTO } from "coreframetypings/dto"
import Loader from '@/Utility/Loader';

export default class ChangePasswordViewModel extends ViewViewModel {
    public allowClose: any;
    /**
    * Handler für das Schließen des Fensters
    */
    public onClose: any;
    public window: WindowViewModelT;
    public labelOldPassword: LabelViewModelT;
    public oldPassword: PasswordBoxViewModelT;
    public labelNewPassword: LabelViewModelT;
    public newPassword: PasswordBoxViewModelT;
    public labelNewPasswordRepeat: LabelViewModelT;
    public newPasswordRepeat: PasswordBoxViewModelT;
    public okay: ButtonViewModelT;
    public cancel: ButtonViewModelT;
    private WindowViewModel = ComponentManager.getViewModel<typeof WindowViewModelT>('WindowViewModel');
    private LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModelT>('LabelViewModel');
    private ButtonViewModel = ComponentManager.getViewModel<typeof ButtonViewModelT>('ButtonViewModel');
    private PasswordBoxViewModel = ComponentManager.getViewModel<typeof PasswordBoxViewModelT>('PasswordBoxViewModel');
    private MessageBoxViewModel = ComponentManager.getViewModel<typeof MessageBoxViewModelT>('MessageBoxViewModel');
    private ImageViewModel = ComponentManager.getViewModel<typeof ImageViewModelT>('ImageViewModel');
    public locator: any;


    constructor(params: any) {
        super(params);

        // Window
        this.allowClose = this.paramOrDefault(params.allowClose, true);
        this.onClose = this.handlerOrDefault(params.onClose);
        this.locator = "changepassword";
        this.window = new this.WindowViewModel({
                isModal: true,
                isOpen: true,
                allowClose: this.allowClose,
                onClose: this.onClose,
                headline: new this.LabelViewModel({
                    ergName: 'MENU_CHANGE_PASSWORD_NAME'
                }),
                locator: this.locator,
                autoFocus: false
        });

        // Old/New Password
        this.labelOldPassword = new this.LabelViewModel({
                ergName: 'CHANGE_PW_OLD_PASSWORD'
            });
        this.oldPassword = new this.PasswordBoxViewModel({
                isFocusable: true,
                parentLocator: this.locator,
                locator: 'oldpassword'
            });
        this.labelNewPassword = new this.LabelViewModel({
                ergName: 'CHANGE_PW_NEW_PASSWORD'
            });
        this.newPassword = new this.PasswordBoxViewModel({
                isFocusable: true,
                parentLocator: this.locator,
                locator: 'newpassword'
            });
        this.labelNewPasswordRepeat =  new this.LabelViewModel({
                ergName: 'CHANGE_PW_NEW_REPEAT_PASSWORD'
            });
        this.newPasswordRepeat =  new this.PasswordBoxViewModel({
                isFocusable: true,
                parentLocator: this.locator,
                locator: 'newpasswordrepeat'
        });

        // Buttons
        this.okay =  new this.ButtonViewModel({
                isFocusable: true,
                isPrimaryButton: true,
                image: new this.ImageViewModel({
                    source: GlobalConfiguration.inlineImages.WINDOW_OK,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'OKAY'
                    })
                }),
                label: new this.LabelViewModel({
                    ergName: 'OKAY'
                }),
            onClick: () => {
                if (this.newPassword.value() === this.newPasswordRepeat.value()) {
                    this.window.increaseBusyCounter();
                    RADEnvironmentProxy.getInstance().changePassword({
                        oldPassword: this.oldPassword.value(),
                        newPassword: this.newPassword.value()
                    }).then((responseDTO) => {
                        this.handlePWChange(responseDTO);
                    }).always(() => {
                        if (this.isDisposed)
                            return
                        this.window.decreaseBusyCounter()
                    });
                } else {
                    RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createErrorMessageBox(null, 'PASSWORD_DISMATCH', null, null, null));
                }
            },
            parentLocator: this.locator,
            locator: 'submit'
        });
        this.cancel = new this.ButtonViewModel({
                isFocusable: true,
                isVisible: this.allowClose,
                label: new this.LabelViewModel({
                    ergName: 'CANCEL'
                }),
                image: new this.ImageViewModel({
                    source: GlobalConfiguration.inlineImages.WINDOW_CANCEL,
                    alternativeText: new this.LabelViewModel({
                        ergName: 'CANCEL'
                    })
                }),
                onClick: () => {
                    this.window.isOpen(false);
                },
                parentLocator: this.locator,
                locator: 'cancel'
            });
    }

    public createErrorMessageBox(messages: Array<string>): MessageBoxViewModelT {
        let hasMessages = messages.length > 0;
        let content = '';
        if (hasMessages) {
            content = ResourceManager.getInstance().get('PASSWORD_CANNOT_CHANGE_WITH_MESSAGES');
            messages.forEach((message) => {
                content += '\r\n- ' + message;
            });
        } else {
            content = ResourceManager.getInstance().get('PASSWORD_CANNOT_CHANGE');
        }
        return this.MessageBoxViewModel.createErrorMessageBox(content, null, null, null, null);
    }

    private handlePWChange(responseDTO: IChangePasswordResponseDTO): void {
        if (responseDTO.successfull) {
            this.onSuccess();
            return;
        }

        if (responseDTO.isUserLocked) {
            RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createErrorMessageBox(null, 'PASSWORD_CANNOT_CHANGE_USERLOCKED', null, null, null));
            return;
        }

        if (responseDTO.twoFATokenNeeded) {
            this.openTwoFATokenDialog();
            return;
        }

        RADApplication.getInstance().showMessageBox(this.createErrorMessageBox(responseDTO.messages));
    }

    private openTwoFATokenDialog(): void {
        let tokenDialogViewFactory: TwoFactorTokenDialogViewFactory = NavigationService.getInstance().loadViewFactoryIsolated('TwoFactorTokenDialogView', {
            infoTextErgName: 'MENU_CHANGE_PASSWORD_TWO_FA_INFO',
            buttonErgname: 'MENU_CHANGE_PASSWORD_TWO_FA_BUTTON',
            shouldCleanRegion: false,
            username: RADApplication.getInstance().user.username(),
            onClose: () => {
                NavigationService.getInstance().removeViewFactoryIsolated(tokenDialogViewFactory);
                if (!this.isDisposed) {
                    this.oldPassword.value('');
                    this.newPassword.value('');
                    this.newPasswordRepeat.value('');
                }
            },
            onSubmit: (token) => {                
                return RADEnvironmentProxy.getInstance().changePassword({
                    oldPassword: this.oldPassword.value() + token,
                    newPassword: this.newPassword.value(),
                    tokenExplicitlyAdded: true
                }).then((data) => {
                    if (data.successfull === true) {
                        this.onSuccess();
                        return true;
                    }

                    RADApplication.getInstance().showMessageBox(this.createErrorMessageBox(data.messages));
                    return false;

                });
            }
        }, true);
    }

    private onSuccess() {
        // Erst die MessageBox anzeigen und dann das ChangePasswordWindow schließen, da sonst disposing angestoßen werden kann
        RADApplication.getInstance().showMessageBox(this.MessageBoxViewModel.createNoticeMessageBox(null, 'PASSWORD_IS_CHANGE'));
        this.window.isOpen(false);
    }
}

ComponentManager.setViewModel('ChangePasswordViewModel', ChangePasswordViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');